(function () {
  if (customElements.get('product-sticky-buy')) return;

  customElements.define(
    'product-sticky-buy',
    class ProductStickyBuy extends HTMLElement {
      connectedCallback() {
        this.productInfo = this.closest('product-info');
        this.proxyButton = this.querySelector('.product-sticky-buy__button');
        this.label = this.querySelector('.product-sticky-buy__label');
        this.mobileQuery = window.matchMedia('(max-width: 749px)');
        this.mainButtonVisible = false;
        this.variantPicker = this.productInfo?.querySelector('variant-selects');
        this.requiresVariantChoice =
          this.dataset.requiresVariantChoice === 'true' &&
          Boolean(
            this.variantPicker?.querySelector(
              'input[type="radio"]:not([disabled]), select:not([disabled])'
            )
          );
        this.variantChoiceConfirmed = !this.requiresVariantChoice;

        if (
          !this.productInfo ||
          this.productInfo.dataset.updateUrl !== 'true' ||
          !this.proxyButton ||
          !this.resolveMainButton()
        ) {
          return;
        }

        this.onProxyClick = this.handleProxyClick.bind(this);
        this.onViewportChange = this.updateVisibility.bind(this);
        this.onVariantInteraction = this.handleVariantInteraction.bind(this);
        this.proxyButton.addEventListener('click', this.onProxyClick);
        this.productInfo.addEventListener('change', this.onVariantInteraction);
        this.productInfo.addEventListener('click', this.onVariantInteraction);

        if (typeof this.mobileQuery.addEventListener === 'function') {
          this.mobileQuery.addEventListener('change', this.onViewportChange);
        } else {
          this.mobileQuery.addListener(this.onViewportChange);
        }

        this.buttonObserver = new MutationObserver(() => {
          this.syncFromMainButton();
          this.updateVisibility();
        });
        this.observeMainButton();

        if ('IntersectionObserver' in window) {
          this.intersectionObserver = new IntersectionObserver(
            (entries) => {
              this.mainButtonVisible = entries.some((entry) => entry.isIntersecting);
              this.updateVisibility();
            },
            { threshold: 0.01 }
          );
          this.intersectionObserver.observe(this.mainButton);
        } else {
          this.onScroll = () => {
            this.mainButtonVisible = this.isMainButtonInViewport();
            this.updateVisibility();
          };
          window.addEventListener('scroll', this.onScroll, { passive: true });
        }

        this.hidden = false;
        this.mainButtonVisible = this.isMainButtonInViewport();
        this.syncFromMainButton();
        this.updateVisibility();
      }

      disconnectedCallback() {
        this.proxyButton?.removeEventListener('click', this.onProxyClick);
        this.productInfo?.removeEventListener('change', this.onVariantInteraction);
        this.productInfo?.removeEventListener('click', this.onVariantInteraction);
        this.buttonObserver?.disconnect();
        this.intersectionObserver?.disconnect();
        if (this.onScroll) window.removeEventListener('scroll', this.onScroll);

        if (this.mobileQuery && this.onViewportChange) {
          if (typeof this.mobileQuery.removeEventListener === 'function') {
            this.mobileQuery.removeEventListener('change', this.onViewportChange);
          } else {
            this.mobileQuery.removeListener(this.onViewportChange);
          }
        }

        this.classList.remove('is-visible');
        this.updateBodyOffset();
      }

      resolveMainButton() {
        const buttonId = this.dataset.mainButtonId;
        const nextButton = buttonId ? document.getElementById(buttonId) : null;

        if (nextButton && nextButton !== this.mainButton) {
          this.mainButton = nextButton;
          this.observeMainButton();
          this.intersectionObserver?.disconnect();
          this.intersectionObserver?.observe(this.mainButton);
        }

        return this.mainButton;
      }

      observeMainButton() {
        if (!this.buttonObserver || !this.mainButton) return;
        this.buttonObserver.disconnect();
        this.buttonObserver.observe(this.mainButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled'],
          childList: true,
          subtree: true,
        });
      }

      isMainButtonInViewport() {
        if (!this.resolveMainButton()) return false;
        const rect = this.mainButton.getBoundingClientRect();
        return rect.bottom > 0 && rect.top < window.innerHeight;
      }

      isMainButtonDisabled() {
        return (
          !this.resolveMainButton() ||
          this.mainButton.disabled ||
          this.mainButton.getAttribute('aria-disabled') === 'true'
        );
      }

      syncFromMainButton() {
        if (!this.resolveMainButton()) return;
        const mainLabel = this.mainButton.querySelector('span');
        if (this.requiresVariantChoice && !this.variantChoiceConfirmed && this.label) {
          this.label.textContent = this.dataset.chooseOptionsLabel || 'Choose options';
        } else if (mainLabel && this.label) {
          this.label.textContent = mainLabel.textContent.trim();
        }
        this.proxyButton.disabled =
          this.variantChoiceConfirmed && this.isMainButtonDisabled();
      }

      updateVisibility() {
        this.syncFromMainButton();
        const shouldShow =
          this.mobileQuery.matches &&
          !this.mainButtonVisible &&
          (!this.variantChoiceConfirmed || !this.isMainButtonDisabled());

        this.classList.toggle('is-visible', shouldShow);
        this.proxyButton.tabIndex = shouldShow ? 0 : -1;
        this.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
        this.updateBodyOffset();
      }

      updateBodyOffset() {
        document.body.classList.toggle(
          'has-product-sticky-buy',
          Boolean(document.querySelector('product-sticky-buy.is-visible'))
        );
      }

      handleProxyClick() {
        this.syncFromMainButton();
        if (!this.variantChoiceConfirmed) {
          this.focusVariantPicker();
          return;
        }
        if (this.isMainButtonDisabled()) return;
        this.mainButton.click();
      }

      handleVariantInteraction(event) {
        const variantPicker = event.target?.closest?.('variant-selects');
        const clickedChoice =
          event.type === 'click' &&
          Boolean(
            event.target?.matches?.('input[type="radio"]') ||
            event.target?.closest?.('label[for]')
          );
        const changedChoice = event.type === 'change';

        if (
          !this.requiresVariantChoice ||
          event.isTrusted !== true ||
          !variantPicker ||
          (!clickedChoice && !changedChoice)
        ) {
          return;
        }

        this.variantChoiceConfirmed = true;
        this.syncFromMainButton();
        this.updateVisibility();
      }

      focusVariantPicker() {
        const variantPicker = this.productInfo.querySelector('variant-selects');
        if (!variantPicker) return;

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        variantPicker.scrollIntoView({
          behavior: reduceMotion ? 'auto' : 'smooth',
          block: 'center',
        });

        const firstControl = variantPicker.querySelector(
          'input:not([disabled]), select:not([disabled])'
        );
        firstControl?.focus({ preventScroll: true });
      }
    }
  );
})();
